import { Alert, Button, CircularProgress, Stack } from '@mui/material'
import { useCallback, useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import {
  useLazyGetProjectUserMatchesQuery,
  useRecordRecommendationInteractionMutation,
} from '../../../../api/recommendations'
import type { RecommendationCandidateUserMatch } from '../../../../api/types'
import {
  RecommendationSection,
  RecommendationUserCard,
  RecommendationsLoadMoreTrigger,
} from '../../../Recommendations/RecommendationCards'
import modalEmitter from '../../../ModalComponent/modalEmitter'
import { InviteProjectModalBody } from '../../../Modals'
import { NotFound } from '../../../ui-kit/NotFound'

const PAGE_SIZE = 20

type ProjectUserMatchesBlockProps = {
  projectId: number
  kind: 'role' | 'resource'
}

function mergeMatches(
  base: RecommendationCandidateUserMatch[],
  next: RecommendationCandidateUserMatch[],
): RecommendationCandidateUserMatch[] {
  const seen = new Set(base.map((match) => `${match.slot.id}:${match.user?.id ?? 'no-user'}`))
  return [
    ...base,
    ...next.filter((match) => {
      const key = `${match.slot.id}:${match.user?.id ?? 'no-user'}`
      if (seen.has(key)) return false
      seen.add(key)
      return true
    }),
  ]
}

export const ProjectUserMatchesBlock = ({ projectId, kind }: ProjectUserMatchesBlockProps) => {
  const [loadMatches, { isLoading, isFetching, isError }] = useLazyGetProjectUserMatchesQuery()
  const [recordInteraction] = useRecordRecommendationInteractionMutation()
  const [hiddenKeys, setHiddenKeys] = useState<Set<string>>(() => new Set())
  const [exactItems, setExactItems] = useState<RecommendationCandidateUserMatch[]>([])
  const [potentialItems, setPotentialItems] = useState<RecommendationCandidateUserMatch[]>([])
  const [nextCursor, setNextCursor] = useState<string | null>(null)
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false)

  const matchKey = (match: RecommendationCandidateUserMatch) => `${match.slot.id}:${match.user?.id ?? 'no-user'}`
  const hideMatch = (match: RecommendationCandidateUserMatch) => setHiddenKeys((current) => new Set(current).add(matchKey(match)))
  const restoreMatch = (match: RecommendationCandidateUserMatch) => {
    setHiddenKeys((current) => {
      const next = new Set(current)
      next.delete(matchKey(match))
      return next
    })
  }

  const loadPage = useCallback(
    async (cursor: string | null) => {
      const response = await loadMatches({ projectId, limit: PAGE_SIZE, cursor, kind }, true).unwrap()
      const exactPage = (response.exact ?? []).filter((match) => match.slot.kind === kind)
      const potentialPage = (response.potential ?? []).filter((match) => match.slot.kind === kind)
      setExactItems((current) => (cursor ? mergeMatches(current, exactPage) : exactPage))
      setPotentialItems((current) => (cursor ? mergeMatches(current, potentialPage) : potentialPage))
      setNextCursor(response.meta?.next_cursor ?? null)
      setHasLoadedOnce(true)
    },
    [kind, loadMatches, projectId],
  )

  useEffect(() => {
    setHiddenKeys(new Set())
    setExactItems([])
    setPotentialItems([])
    setNextCursor(null)
    setHasLoadedOnce(false)
    void loadPage(null)
  }, [loadPage])

  const exact = exactItems.filter((match) => !hiddenKeys.has(matchKey(match)))
  const potential = potentialItems.filter((match) => !hiddenKeys.has(matchKey(match)))
  const items = [...exact, ...potential]
  const isEmpty = items.length === 0
  const isResourceKind = kind === 'resource'
  const emptyMessage = isResourceKind
    ? 'Пока нет подходящих людей для ресурсов'
    : 'Пока нет подходящих людей'
  const errorMessage = isResourceKind
    ? 'Не удалось загрузить подходящих людей для ресурсов.'
    : 'Не удалось загрузить подходящих людей.'

  const dismiss = async (match: RecommendationCandidateUserMatch) => {
    if (!match.user?.id) return

    hideMatch(match)
    try {
      await recordInteraction({
        direction: 'for_project',
        context_type: 'project',
        context_id: projectId,
        target_type: 'user',
        target_id: match.user.id,
        action: 'dismissed',
      }).unwrap()
    } catch {
      restoreMatch(match)
      toast.error('Не удалось скрыть рекомендацию')
    }
  }

  const act = (match: RecommendationCandidateUserMatch) => {
    if (!match.user?.id) return

    const isResource = match.slot.kind === 'resource'

    modalEmitter.emitOpenModal({
      dialog: {
        title: isResource ? 'Обсудить ресурс' : 'Пригласить в проект',
        body: (
          <InviteProjectModalBody
            userId={match.user.id}
            projectId={projectId}
            projectName={match.project?.name}
            introText={
              isResource
                ? `Предложите обсудить ресурс "${match.slot.title}" для проекта.`
                : `Пригласите пользователя на роль "${match.slot.title}" в проект.`
            }
            messagePlaceholder={
              isResource
                ? 'Напишите, какой ресурс нужен и как удобно обсудить условия'
                : 'Напишите кратко, почему приглашаете и какая роль нужна'
            }
            submitLabel={isResource ? 'Отправить предложение' : 'Отправить приглашение'}
            successMessage={isResource ? 'Предложение обсудить ресурс отправлено' : 'Приглашение отправлено'}
          />
        ),
      },
      closeAtion: { visible: false },
      confirmAtion: { visible: false },
    })
  }

  if (isLoading || (isFetching && !hasLoadedOnce)) {
    return (
      <Stack alignItems="center" py={3}>
        <CircularProgress size={24} />
      </Stack>
    )
  }

  if (isError) {
    return <Alert severity="error">{errorMessage}</Alert>
  }

  if (isEmpty) {
    return <NotFound text={emptyMessage} />
  }

  return (
    <Stack gap={2}>
      <RecommendationSection
        title="Могут подойти"
        items={items}
        renderItem={(match) => (
          <RecommendationUserCard
            key={`project-match-${match.slot.id}-${match.user?.id}`}
            match={match}
            isDismissing={false}
            onDismiss={() => dismiss(match)}
            primaryAction={
              <Button
                variant="follow"
                size="small"
                disabled={!match.user}
                onClick={() => act(match)}
              >
                {match.slot.kind === 'resource' ? 'Обсудить ресурс' : 'Пригласить'}
              </Button>
            }
          />
        )}
      />
      <RecommendationsLoadMoreTrigger
        hasMore={Boolean(nextCursor)}
        isLoading={isFetching}
        onLoadMore={() => {
          if (!nextCursor || isFetching) return
          void loadPage(nextCursor)
        }}
      />
    </Stack>
  )
}
